import { useQuery } from "@tanstack/react-query";
import {
  fetchFuturesHistory,
  fetchGasSpotSeries,
  fetchGasStorage,
  type FuturesHistoryPayload,
  type GasSpotSeriesPayload,
  type GasStoragePayload,
} from "../lib/api";
import { FuturesCurveChart } from "./FuturesCurveChart";
import { GasSpotChart } from "./GasSpotChart";
import { GasStorageChart } from "./GasStorageChart";

export function GasOverviewTab() {
  // See PowerOverviewTab — overview data uses a 1 h staleTime + window-focus
  // revalidation so a long-lived tab picks up post-rollover contracts, but
  // no polling (customer-meeting-friendly).
  const futures = useQuery<FuturesHistoryPayload, Error>({
    queryKey: ["futures-history"],
    queryFn: () => fetchFuturesHistory(false),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 60 * 1000,
  });
  const spot = useQuery<GasSpotSeriesPayload, Error>({
    queryKey: ["gas-spot-series"],
    queryFn: () => fetchGasSpotSeries(false),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 60 * 1000,
  });
  const storage = useQuery<GasStoragePayload, Error>({
    queryKey: ["gas-storage"],
    queryFn: () => fetchGasStorage(false),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 60 * 1000,
  });

  const gasCurve = futures.data?.curves.find((c) => c.id === "gas");

  return (
    <div className="space-y-4 sm:space-y-5">
      {futures.isError && (
        <ErrorBanner
          title="Terminmarkt-Historie konnte nicht geladen werden"
          message={futures.error.message}
          onRetry={() => futures.refetch()}
        />
      )}
      {futures.isLoading ? (
        <ChartSkeleton />
      ) : (
        gasCurve && <FuturesCurveChart curve={gasCurve} updatedAt={futures.data!.updatedAt} />
      )}

      {spot.isError && (
        <ErrorBanner
          title="EGSI/Forward konnte nicht geladen werden"
          message={spot.error.message}
          onRetry={() => spot.refetch()}
        />
      )}
      {spot.isLoading ? <ChartSkeleton tall /> : spot.data && <GasSpotChart data={spot.data} />}

      {storage.isError && (
        <ErrorBanner
          title="Gasspeicherstände konnten nicht geladen werden"
          message={storage.error.message}
          onRetry={() => storage.refetch()}
        />
      )}
      {storage.isLoading ? (
        <ChartSkeleton tall />
      ) : (
        storage.data && <GasStorageChart data={storage.data} />
      )}
    </div>
  );
}

function ChartSkeleton({ tall = false }: { tall?: boolean }) {
  return (
    <div
      className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 animate-pulse ${
        tall ? "h-80" : "h-72"
      }`}
    />
  );
}

function ErrorBanner({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-lg border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-4 py-3 text-sm text-rose-900 dark:text-rose-200 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium truncate">{title}</p>
        <p className="text-xs opacity-80 truncate">{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 rounded-md bg-rose-900 text-white px-2.5 py-1 text-xs font-medium hover:bg-rose-800"
      >
        Erneut
      </button>
    </div>
  );
}
